import { useEffect, useState } from "react";
import { userApi } from "../../services/userApi";
import { useToast } from "../../context/ToastContext";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { useHelpDisclosure } from "../../components/common/HelpDisclosure";

// Named intervals rather than a bare number box. "14" alone does not say
// what it governs or which direction is better, and the same reasoning
// applied to the supplier rating in 78985d2. Custom stays available because
// beekeeping cadence is genuinely personal and a fixed list would be wrong
// for someone.
const PRESETS = [
  { days: 7, label: "Weekly", note: "Swarm season, or a colony you're watching closely" },
  { days: 10, label: "Every 10 days", note: "A common compromise through the active season" },
  { days: 14, label: "Every 2 weeks", note: "The default. A reasonable in-season rhythm" },
  { days: 21, label: "Every 3 weeks", note: "Established colonies, settled conditions" },
  { days: 30, label: "Monthly", note: "Quieter months, or a hands-off approach" },
];

/*
 * A different range, because the useful winter cadence is longer and the
 * checks are different in kind -- hefting for stores, mouse guards,
 * ventilation, clearing an entrance -- rather than inspections.
 *
 * 30 is the default and it is NOT advice: it is the number the open-season
 * list above already calls "Quieter months, or a hands-off approach", so
 * the closed season introduces no new claim about how to overwinter bees.
 */
const CLOSED_PRESETS = [
  { days: 21, label: "Every 3 weeks", note: "A mild winter, or hives you can see from the house" },
  { days: 30, label: "Monthly", note: "The default. Enough to catch stores running low" },
  { days: 45, label: "Every 6 weeks", note: "A long, settled cold spell" },
  { days: 60, label: "Every 2 months", note: "Deep winter, hives wrapped and left alone" },
];

// Module scope, with Schedule below: both are read by it, and a style
// object rebuilt every render is a new object identity for no reason.
const section = {
  display: "grid",
  gap: "var(--space-2)",
  paddingBottom: "var(--space-4)",
  borderBottom: "1px solid var(--color-border)",
  marginBottom: "var(--space-4)",
};
const headingRow = { display: "flex", gap: "var(--space-2)", alignItems: "center" };

/*
 * HOISTED OUT OF THE COMPONENT, and that is not a style preference.
 *
 * Defined inside the render, this is a NEW function identity every
 * time the parent renders, so React sees a different component type
 * and unmounts the whole subtree rather than updating it. The custom
 * number box lost focus after every keystroke -- setCustom re-rendered
 * the parent, which remounted the input the user was typing into.
 *
 * It reached a test as "typed 400, nothing was sent": only the first
 * character survived. A remount is invisible until state lives inside
 * the thing being remounted.
 */
function Schedule({ id, title, presets, value, onPick, help, custom: isCustom, showOther, subtitle, saving }) {
    const known = !showOther && presets.some((p) => p.days === value);
    return (
      /* role="group" with the heading as its name. Sighted users have the
         heading; a screen reader moving control to control would
         otherwise meet two identically-labelled "Check every" selects
         with nothing saying which season each belongs to. The old
         button lists carried this for the same reason. */
      <div style={section} role="group" aria-label={title}>
        <span style={headingRow}>
          <h3 style={{ margin: 0, fontSize: "var(--font-size-md)" }}>{title}</h3>
          {help.button}
        </span>
        {help.panel(help.body)}
        {/* VISIBLE, not behind the "?". The rest of the prose moved
            there, but this is a CORRECTION of a likely wrong assumption
            rather than an explanation of the control -- somebody who
            reads "closed" as "reminders off" is choosing something
            deliberately not on offer, and they will not press a "?" to
            find that out because they do not know they are wrong. */}
        {subtitle}
        <label htmlFor={id} style={{ fontSize: "var(--font-size-sm)", fontWeight: 600 }}>
          Check every
        </label>
        <select
          id={id}
          className="touch-target"
          disabled={saving}
          value={known ? String(value) : "other"}
          onChange={(e) => onPick(e.target.value)}
          style={{ display: "block", maxWidth: "22rem" }}
        >
          {presets.map((p) => (
            <option key={p.days} value={p.days}>{`${p.days} days — ${p.label}`}</option>
          ))}
          <option value="other">Other…</option>
        </select>
        {/* Only when it IS other, and pre-filled with the current value
            so somebody on 12 days sees 12 rather than an empty box that
            reads as "your setting is gone". */}
        {isCustom}
      </div>
    );
  }


export function InspectionScheduleTab() {
  const { notify } = useToast();
  const [interval, setIntervalDays] = useState(null);
  const [custom, setCustom] = useState("");
  // #377. `interval` is the OPEN-season number this tab edits; `effective`
  // is what the app is actually using today, which differs whenever the
  // closed season is running.
  const [closedInterval, setClosedInterval] = useState(null);
  const [effective, setEffective] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    userApi
      .getInspectionInterval()
      .then((r) => {
        /*
         * open_interval_days, NOT interval_days.
         *
         * interval_days is the cadence in force TODAY, which is the closed
         * one all winter. Reading it into this control would show 30 as
         * the open-season setting and then SAVE it there on the next
         * press -- quietly overwriting a real 14 with a winter number,
         * and only for the users who had turned the season on.
         */
        applyCadence(r);
        const open = r.open_interval_days ?? r.interval_days;
        if (!PRESETS.some((p) => p.days === open)) setCustom(String(open));
      })
      .catch(() => notify("Could not load your inspection schedule", "danger"))
      .finally(() => setLoading(false));
  }, [notify]);

  /* One reader for the cadence payload, used by both the load and every
     save, so the two can never disagree about which field is which. */
  function applyCadence(r) {
    setIntervalDays(r.open_interval_days ?? r.interval_days);
    setClosedInterval(r.closed_interval_days ?? null);
    setEffective(r.interval_days);
  }

  async function saveSeason(opts, message) {
    setSaving(true);
    try {
      applyCadence(await userApi.setInspectionInterval(interval, opts));
      notify(message, "success");
    } catch (err) {
      notify(err.apiMessage || "Could not save your closed-season schedule", "danger");
    } finally {
      setSaving(false);
    }
  }

  async function save(days) {
    // Bounds mirror CK_Users_inspection_interval (V034). Checking here as
    // well as server-side is not redundant -- it turns a round trip into an
    // immediate answer, and the constraint is what actually guarantees it.
    if (!Number.isInteger(days) || days < 1 || days > 365) {
      notify("Choose between 1 and 365 days", "warning");
      return;
    }
    setSaving(true);
    try {
      applyCadence(await userApi.setInspectionInterval(days));
      notify(`Hives will be flagged after ${days} day${days === 1 ? "" : "s"}`, "success");
    } catch (err) {
      notify(err.apiMessage || "Could not save your inspection schedule", "danger");
    } finally {
      setSaving(false);
    }
  }

  /* "I chose Other" is its own state, because `interval` cannot carry
     it: picking Other leaves the saved value on 14 until a number is
     typed, so deriving the flag from the value meant the input never
     appeared and there was no way to enter a custom number at all. */
  const [openOther, setOpenOther] = useState(false);
  const openHelp = useHelpDisclosure("the open season schedule");
  const closedHelp = useHelpDisclosure("the closed season schedule");

  if (loading) return <p>Loading…</p>;

  /*
   * A HEADING, A CONTROL, AND THE EXPLANATION ONE PRESS AWAY (#436).
   *
   * #429 cut this screen from 189 words to 69 and it was still reported
   * as "a LOT of verbiage". Trimming prose was the wrong lever: the
   * structure was the problem. Every paragraph sat in the reading path
   * of somebody who had come to change one number.
   *
   * Asked for, and it is the right shape: "put the heading and put a '?'
   * with all the text explaining what it means ... and then at the bottom
   * a card that clearly explains the current selections."
   *
   * Nothing is deleted. The copy #429 fought to keep -- that closed is a
   * longer schedule and not a pause, that there is no right answer, that
   * this drives every "due" in the app -- all survives, behind the "?"
   * belonging to the section it is about.
   *
   * A DROPDOWN WITH "OTHER", also asked for. Nine buttons carrying nine
   * notes were most of the remaining height, and the notes were the part
   * nobody could avoid reading. A select collapses them to one line and
   * keeps the custom value that made the old free-text box necessary.
   */

  const openBody = (
    <>
      <span>
        How long after a check a hive is flagged as due. This drives every
        “due” and “overdue” in the app.
      </span>
      {/* The one line here that is not describing the UI: it declines to
          give beekeeping advice, which is a product position. 14 is what
          the app assumed before this setting existed, not a
          recommendation. */}
      <span>There is no right answer — it depends on your season, climate and how you work.</span>
    </>
  );

  /* NO DATE HERE (#389): hives are closed individually, because
     migratory keepers shut half an operation and work the rest. What IS
     an account preference is how often to check a hive while it is shut
     -- thirty hives wintering in Florida do not each need their own
     answer. */
  const closedBody = (
    <span>
      Closing belongs to a hive, not to the account, so this sets only how often a
      closed hive is checked. Which hives are closed, and until when, is decided
      per hive.
    </span>
  );

  /* A CONTROL, NOT PROSE, so it stays out of the disclosure. #429 turned
     thirty-seven words explaining where to close a hive into a link that
     goes there, for the reason recorded then: a setting with no control
     beside it reads as broken. Burying the link behind a "?" would
     recreate exactly that. */
  const closedSubtitle = (
    <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
      A longer schedule, not a pause — winter still has real checks.{" "}
      <a href="/hives">Close hives for the season</a> from the hive list.
    </span>
  );

  return (
    <Card title="Inspection schedule">
      <Schedule
        id="open-schedule"
        title="Open season schedule"
        presets={PRESETS}
        value={interval}
        help={{ ...openHelp, body: openBody }}
        onPick={(v) => {
          if (v === "other") { setOpenOther(true); setCustom(String(interval ?? "")); }
          else { setOpenOther(false); setCustom(""); save(Number(v)); }
        }}
        showOther={openOther}
        saving={saving}
        custom={(openOther || !PRESETS.some((p) => p.days === interval)) ? (
          <span style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
            <input id="inspection-schedule-tab-custom" name="custom"
              type="number" min={1} max={365} value={custom} aria-label="Days between checks"
              onChange={(e) => setCustom(e.target.value)}
              className="touch-target" style={{ width: "8rem" }}
            />
            <Button variant="secondary" disabled={saving || !custom} onClick={() => save(Number(custom))}>
              Save
            </Button>
          </span>
        ) : null}
      />

      <Schedule
        id="closed-schedule"
        title="Closed season schedule"
        presets={CLOSED_PRESETS}
        value={closedInterval}
        help={{ ...closedHelp, body: closedBody }}
        subtitle={closedSubtitle}
        saving={saving}
        onPick={(v) => { if (v !== "other") saveSeason({ closedIntervalDays: Number(v) }, `Checking every ${v} days while closed`); }}
        custom={null}
      />

      {/*
        * THE ANSWER TO "DID THAT WORK", asked for as "a card that clearly
        * explains the current selections".
        *
        * THE SCOPING CLAUSE STAYS. `effective` is the cadence in force
        * TODAY, which is the CLOSED number all winter while the control
        * above still reads 14. Without naming which hives it applies to,
        * this quotes 30 beside a control showing 14 and simply reads as a
        * bug -- the exact confusion #377 added it to resolve, and the
        * clause #429's first pass cut as padding before a test caught it.
        */}
      <div style={{
        background: "var(--color-surface-alt, var(--color-bg-subtle, transparent))",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-md, 6px)",
        padding: "var(--space-3)",
        display: "grid",
        gap: "var(--space-1)",
      }}>
        <strong>Right now</strong>
        <span>
          Open hives are flagged <strong>{effective} day{effective === 1 ? "" : "s"}</strong> after
          their last check.
        </span>
        {closedInterval ? (
          <span style={{ color: "var(--color-text-muted)" }}>
            Hives closed for the season are checked every <strong>{closedInterval} days</strong> instead.
          </span>
        ) : null}
      </div>
    </Card>
  );
}
